import { useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  BoldIcon,
  Heading2Icon,
  Heading3Icon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  TextQuoteIcon,
} from 'lucide-react';
import { cn } from '../cn';
import { articleDocFromEditor, isSafeHref, type RichTextDoc } from '../rich-text/doc';

export interface ArticleEditorProps {
  /** The article to start from; read once, at mount. */
  readonly initialDoc?: RichTextDoc | null | undefined;
  /** Called with the article, in the portal vocabulary, on every change. */
  readonly onChange: (doc: RichTextDoc) => void;
  readonly ariaLabel: string;
  /** Points the editing surface at its label and hint. */
  readonly id?: string | undefined;
  readonly describedBy?: string | undefined;
  readonly className?: string | undefined;
}

/** The starting article as the editor's own JSON, or an empty editor. */
function startingContent(doc: RichTextDoc | null | undefined): JSONContent | string {
  if (!doc) return '';
  const content: JSONContent = JSON.parse(JSON.stringify(doc));
  return content;
}

/** A link's address as typed, with https:// added when the writer left the scheme off. */
function withScheme(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * The Resource center's article editor: section headings, bold, italics,
 * lists, quotes and links, the vocabulary the portal renders. It hands back
 * a {@link RichTextDoc}, never HTML, narrowed by `articleDocFromEditor` so a
 * pasted image or table never reaches the API.
 *
 * @param props - The starting article and the change handler.
 * @returns The editor with its toolbar.
 */
export function ArticleEditor({
  initialDoc,
  onChange,
  ariaLabel,
  id,
  describedBy,
  className,
}: ArticleEditorProps): React.ReactElement {
  // Re-render on every transaction so the toolbar reflects the selection.
  const [, setTick] = useState(0);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        code: false,
        horizontalRule: false,
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { target: '_blank', rel: 'noreferrer noopener' },
        },
      }),
    ],
    content: startingContent(initialDoc),
    editorProps: {
      attributes: {
        class: 'portal-prose portal-article min-h-[320px] px-4 py-3 focus:outline-none',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel,
        ...(id ? { id } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      },
    },
    onUpdate: ({ editor: current }) => onChange(articleDocFromEditor(current.getJSON())),
    onTransaction: () => setTick((tick) => tick + 1),
  });

  if (!editor) return <div className="min-h-[360px] rounded-portal-input border border-[#cad3df] bg-white" />;

  /** Asks for an address and links the selection; an empty answer unlinks. */
  function setLink(): void {
    if (!editor) return;
    const previous = editor.getAttributes('link')['href'];
    const answer = window.prompt('Link address', typeof previous === 'string' ? previous : 'https://');
    if (answer === null) return;
    const href = withScheme(answer);
    if (href === '' || !isSafeHref(href)) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  }

  const tools: readonly { label: string; icon: React.ReactNode; active: boolean; run: () => void }[] = [
    {
      label: 'Section heading',
      icon: <Heading2Icon aria-hidden className="size-4" />,
      active: editor.isActive('heading', { level: 2 }),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: 'Subheading',
      icon: <Heading3Icon aria-hidden className="size-4" />,
      active: editor.isActive('heading', { level: 3 }),
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    { label: 'Bold', icon: <BoldIcon aria-hidden className="size-4" />, active: editor.isActive('bold'), run: () => editor.chain().focus().toggleBold().run() },
    { label: 'Italic', icon: <ItalicIcon aria-hidden className="size-4" />, active: editor.isActive('italic'), run: () => editor.chain().focus().toggleItalic().run() },
    {
      label: 'Bulleted list',
      icon: <ListIcon aria-hidden className="size-4" />,
      active: editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Numbered list',
      icon: <ListOrderedIcon aria-hidden className="size-4" />,
      active: editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      label: 'Quote',
      icon: <TextQuoteIcon aria-hidden className="size-4" />,
      active: editor.isActive('blockquote'),
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
    { label: 'Link', icon: <LinkIcon aria-hidden className="size-4" />, active: editor.isActive('link'), run: setLink },
  ];

  return (
    <div className={cn('rounded-portal-input border border-[#cad3df] bg-white focus-within:border-brand', className)}>
      <div role="toolbar" aria-label="Article formatting" className="flex flex-wrap items-center gap-1 border-b border-portal-line px-2 py-1.5">
        {tools.map((tool) => (
          <button
            key={tool.label}
            type="button"
            aria-label={tool.label}
            title={tool.label}
            aria-pressed={tool.active}
            // Before blur, so the editor keeps its selection for the command.
            onMouseDown={(event) => event.preventDefault()}
            onClick={tool.run}
            className={cn(
              'grid size-8 place-items-center rounded-portal-button',
              tool.active ? 'bg-brand-tint text-brand' : 'text-portal-muted hover:bg-portal-sky hover:text-portal-ink',
            )}
          >
            {tool.icon}
          </button>
        ))}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
